import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fmtQty, ROLE_LABEL } from '../shared/domain';
import { ApiError, makeData, post, queryClient, useBootstrap } from './api';
import { Ctx, Err, Spinner, errMsg, pl, useApp, type AppCtx } from './components/ui';
import { AddDialog, ItemDialog, ReturnDialog, TakeDialog } from './components/dialogs';
import { Inventory } from './pages/Inventory';
import { Places } from './pages/Places';
import { PasswordForm, Settings } from './pages/Settings';

export function App() {
  const boot = useBootstrap();
  if (boot.isLoading) return <Spinner />;
  if (boot.error instanceof ApiError && boot.error.status === 401) return <Login />;
  if (boot.error || !boot.data)
    return (
      <div className="login">
        <div className="panel">
          <h1 style={{ fontSize: 24 }}>The app cannot load</h1>
          <p className="meta">{errMsg(boot.error)}</p>
          <button className="btn primary" onClick={() => void boot.refetch()}>
            Try again
          </button>
        </div>
      </div>
    );
  return <Shell />;
}

function Brand({ big }: { big?: boolean }) {
  return (
    <span className={`brand${big ? ' login-brand' : ''}`}>
      <img src="/logo.webp" alt="" className={`logo${big ? ' big' : ''}`} />
      <span>
        Boghche<small>PANTOhealth warehouse</small>
      </span>
    </span>
  );
}

function Login() {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const login = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      await post('/api/login', { name, password });
      await queryClient.resetQueries();
    } catch (x) {
      setErr(errMsg(x));
      setPassword('');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="login">
      <Brand big />
      <form className="panel" onSubmit={(e) => void login(e)}>
        <h1 style={{ fontSize: 26 }}>Sign in</h1>
        <p className="meta" style={{ margin: '0 0 14px' }}>
          Everything you change is saved with your name, the date and the time.
        </p>
        <label className="field">
          Name
          <input autoComplete="username" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>
        <label className="field" style={{ marginTop: 10 }}>
          Password
          <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
        <button className="btn primary" style={{ marginTop: 14, width: '100%' }} disabled={busy || !name.trim() || !password}>
          Sign in
        </button>
        <Err e={err} />
      </form>
    </div>
  );
}

/** Users moved over from the PIN sign-in choose a password first. */
function ChoosePassword({ name }: { name: string }) {
  return (
    <div className="login">
      <Brand big />
      <div className="panel">
        <h1 style={{ fontSize: 24 }}>Choose a password</h1>
        <p className="meta">Hello {name}. Boghche now uses passwords instead of PINs. Please choose your new password to continue.</p>
        <PasswordForm forced />
        <button
          className="linkbtn meta"
          style={{ marginTop: 12 }}
          onClick={async () => {
            await post('/api/logout').catch(() => undefined);
            await queryClient.resetQueries();
          }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

type Dlg = { kind: 'item' | 'add' | 'take'; id: number } | { kind: 'return'; allocId: number } | null;

function Bell() {
  const { d, openItem } = useApp();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const out = (e: MouseEvent) => box.current && !box.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', out);
    return () => document.removeEventListener('mousedown', out);
  }, []);
  const n = d.alerts.length;
  return (
    <div className="bell" ref={box}>
      <button className={`btn iconbtn${n ? ' alarm' : ''}`} onClick={() => setOpen((x) => !x)} aria-label={`Notifications: ${n}`} aria-expanded={open}>
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
        {n > 0 && <span className="cnt">{n}</span>}
      </button>
      {open && (
        <div className="bellpop" role="dialog" aria-label="Notifications">
          <b>{n ? `${pl(n, 'item')} at or below the minimum` : 'No notifications'}</b>
          {!n && <p className="meta">You get a notification here when an item reaches its minimum quantity.</p>}
          {d.alerts.map((i) => (
            <button
              key={i.id}
              className="alertrow"
              onClick={() => {
                setOpen(false);
                openItem(i.id);
              }}
            >
              <span>{i.name}</span>
              <span className="tag bad">
                {fmtQty(i.qty)} left · min {fmtQty(i.minQty!)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Shell() {
  const boot = useBootstrap();
  const d = useMemo(() => makeData(boot.data!), [boot.data]);
  const [dlg, setDlg] = useState<Dlg>(null);
  const [settings, setSettings] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const toastT = useRef<ReturnType<typeof setTimeout>>(undefined);
  const toast = useCallback((m: string) => {
    setToastMsg(m);
    clearTimeout(toastT.current);
    toastT.current = setTimeout(() => setToastMsg(''), 2600);
  }, []);
  const ctx: AppCtx = {
    d,
    toast,
    openItem: (id) => setDlg({ kind: 'item', id }),
    openAdd: (id) => setDlg({ kind: 'add', id }),
    openTake: (id) => setDlg({ kind: 'take', id }),
    openReturn: (allocId) => setDlg({ kind: 'return', allocId }),
  };
  if (d.me.mustChangePassword) return <ChoosePassword name={d.me.name} />;
  const close = () => setDlg(null);
  const item = dlg && dlg.kind !== 'return' ? d.item(dlg.id) : undefined;
  const alloc = dlg?.kind === 'return' ? d.allocs.find((a) => a.id === dlg.allocId) : undefined;
  return (
    <Ctx.Provider value={ctx}>
      <div className="page">
        <header className="top">
          <a className="brand-link" href="/" aria-label="Boghche home">
            <Brand />
          </a>
          <span className="grow" />
          <span className="who meta">
            {d.me.name} · {ROLE_LABEL[d.me.role]}
          </span>
          <Bell />
          <button className="btn iconbtn" onClick={() => setSettings(true)} aria-label="Settings" title="Settings">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </button>
        </header>
        <main>
          <Inventory />
          <Places />
        </main>
      </div>
      {dlg?.kind === 'item' && item && <ItemDialog key={item.id} item={item} onClose={close} />}
      {dlg?.kind === 'add' && item && <AddDialog item={item} onClose={close} />}
      {dlg?.kind === 'take' && item && <TakeDialog item={item} onClose={close} />}
      {dlg?.kind === 'return' && alloc && <ReturnDialog alloc={alloc} item={alloc.item} onClose={close} />}
      {settings && <Settings onClose={() => setSettings(false)} />}
      <div className={`toast ${toastMsg ? 'on' : ''}`} role="status">
        {toastMsg}
      </div>
    </Ctx.Provider>
  );
}
